import { Component, OnInit, ViewChild } from '@angular/core';
import { ForgotPasswordService } from '../../services/forgot-password.service';
import { LogService } from '../../services/log.service';
import { PasswordPolicyService } from '../../services/password-policy.service';
import { BaseForm } from '../../interfaces/BaseForm';
import { Errors } from '../../errors/errors';
import { RouterService } from '../../services/router.service';
import { ErrorMessage } from '../../errors/error-message';
import { PasswordPolicy } from '../../types/password-policy';
import { map } from 'rxjs/operators';
import { CustomValidators } from './forgot-password.customvalidators';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

@Component({
  selector: 'app-forgot-password-confirm',
  templateUrl: './forgot-password-confirm.component.html'
})
export class ForgotPasswordConfirmComponent extends BaseForm implements OnInit {
  code: string;
  new_password: string;
  confirm_password: string;
  submittedPassword: string;
  username: string;
  requireUsername: boolean;
  cognitoPasswordPolicy: PasswordPolicy;
  public forgotPasswordConfirmForm : FormGroup;
  @ViewChild('usernameInput')
  usernameInput;

  @ViewChild('codeInput')
  codeInput;

  constructor(
    private passwordService: ForgotPasswordService,
    private log: LogService,
    private passwordPolicy: PasswordPolicyService,
    private router: RouterService,
    public fb: FormBuilder
  ) {
    super(log);
    this.forgotPasswordConfirmForm  = this.PasswordConfirmChangeForm();
  }

  ngOnInit() {
    this.forgotPasswordConfirmForm  = this.PasswordConfirmChangeForm();
    this.requireUsername = !this.passwordService.getUsernameInProgress();
    this.clear();

    const showPolicyFailure = new ErrorMessage(awsMessage => {
      return `${this.cognitoPasswordPolicy.displayCognitoPolicy()} ${this.cognitoPasswordPolicy.validateAgainstCognitoPolicy(
        this.submittedPassword
      )}`;
    });

    const showFailedCodeOrUser = new ErrorMessage(awsMessage => {
      return this.requireUsername
        ? // If in the workflow where the user has to enter their username,
          // respond to a bad username or code in the same way
          'Invalid username or authorization code. Please try again.'
        : // Otherwise this should only be a code mismatch
          Errors.CODE_MISMATCH.message;
    });
    this.errorHandler.add(Errors.NOT_AUTHORIZED, showFailedCodeOrUser);
    this.errorHandler.add(Errors.CODE_MISMATCH, showFailedCodeOrUser);
    this.errorHandler.add(Errors.EXPIRED_CODE);
    this.errorHandler.add(Errors.INVALID_PARAMETER, showPolicyFailure);
    this.errorHandler.add(Errors.INVALID_PARAMETER_TYPE, showPolicyFailure);
    this.errorHandler.add(Errors.INVALID_PASSWORD, showPolicyFailure);
    this.errorHandler.add(Errors.LIMIT_EXCEEDED);
    this.errorHandler.add(
      Errors.PASSWORD_EXCEPTION,
      new ErrorMessage(message => message)
    );
  }

  PasswordConfirmChangeForm(): FormGroup {
    return this.fb.group(
      {
        password: [
          null,
          Validators.compose([
            Validators.required,
            // check whether the entered password has a number
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true,
            }),
            // check whether the entered password has upper case letter
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true
            }),
            // check whether the entered password has a lower case letter
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true
            }),
            // check whether the entered password has a special character
            CustomValidators.patternValidator(
              /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
              {
                hasSpecialCharacters: true
              }
            ),
            Validators.minLength(8)
          ])
        ],
        confirmPassword: [null, Validators.compose([Validators.required])]
      },
      {
        // check whether our password and confirm password match
        validator: CustomValidators.passwordMatchValidator
      }
    );
  }

  isValid(): boolean {
    if (
      this.isSubmitInProgress ||
      !this.code ||
      !this.new_password ||
      !this.confirm_password
    ) {
      return false;
    }
    return true;
  }

  clear() {
    this.code = '';
    this.new_password = '';
    this.confirm_password = '';
    if (this.requireUsername) {
      this.usernameInput.nativeElement.focus();
    } else {
      this.codeInput.nativeElement.focus();
    }
  }

  submit() {
    this.isSubmitInProgress = true;

    this.errorMsg = this.passwordPolicy.validate(
      this.new_password,
      this.confirm_password
    );
    if (this.errorMsg) {
      this.isSubmitInProgress = false;
      //this.clear();
      return;
    }

    this.submittedPassword = this.new_password;

    const submitConfirm = () => {
      this.passwordService.confirmForgotPassword(
        this.code,
        this.new_password,
        this.username,
        (err, data) => {
          //this.clear();
          this.isSubmitInProgress = false;

          if (err) {
            this.onErrorCallback(err);
          } else {
            this.clear();
            this.isSuccess = true;
            this.startTimer(() => {
              this.router.gotoLogin();
            });
          }
        }
      );
    };

    // Ensure the password policy is fetched for the current username before submitting the change
    this.passwordPolicy
      .getPasswordPolicy(
        null,
        this.username || this.passwordService.getUsernameInProgress()
      )
      .pipe(map(policy => (this.cognitoPasswordPolicy = policy)))
      .subscribe(submitConfirm, submitConfirm);
  }
}
